import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { fetchWithAuth } from '../../helpers';
import { getApiUrl } from '../../config/api';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { classActions, notificationActions } from '../../redux/actions';
import { giftService } from '../../services/gift.service';

const HEADERS = { 'ngrok-skip-browser-warning': '69420' };
const fadeIn = keyframes`from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:none}`;

// ── Styled ────────────────────────────────────────────────────────────────────
const Wrap = styled.div`max-width: 860px; margin: 0 auto;`;
const Card = styled.div`
  background: #fff;
  border: 1.5px solid #e2e8f0;
  border-radius: 16px; padding: 24px; margin-bottom: 20px;
  box-shadow: 0 2px 12px rgba(0,0,0,.06);
`;
const Label = styled.label`display:block;font-size:12px;font-weight:700;color:#64748b;margin-bottom:6px;letter-spacing:.4px;`;
const Select = styled.select`
  width:100%;padding:10px 14px;
  background:white;
  border:1.5px solid #e2e8f0;
  border-radius:10px;color:#1e293b;font-size:14px;
  option{background:white;color:#1e293b;}
  &:focus{outline:none;border-color:#2563eb;}
`;
const Empty = styled.div`text-align:center;color:#64748b;padding:40px 0;`;
const StudentRow = styled.div`
  display:flex;align-items:center;gap:12px;padding:10px 0;
  border-bottom:1px solid #f1f5f9;
  animation:${fadeIn} .2s ease;
  &:last-child{border-bottom:none;}
`;
const Avatar = styled.div`
  width:38px;height:38px;border-radius:50%;background:linear-gradient(135deg,#2563eb,#3b82f6);
  display:flex;align-items:center;justify-content:center;font-size:16px;flex-shrink:0;color:white;font-weight:700;
`;
const StudentName = styled.div`font-size:14px;font-weight:600;color:#1e293b;flex:1;min-width:0;`;
const AbsentBadge = styled.span`
  padding:2px 8px;border-radius:20px;font-size:11px;font-weight:700;
  background:#fee2e2;color:#dc2626;border:1px solid #fca5a5;
`;


const GiftPickerWrap = styled.div`
  position: relative; min-width: 220px; flex-shrink: 0;
`;
const GiftPickerBtn = styled.button<{$hasGift:boolean}>`
  display:flex;align-items:center;gap:8px;padding:6px 10px;
  background:${p=>p.$hasGift?'#d1fae5':'white'};
  border:1.5px solid ${p=>p.$hasGift?'#6ee7b7':'#e2e8f0'};
  border-radius:10px;color:#1e293b;font-size:13px;cursor:pointer;
  width:100%;text-align:left;transition:all .15s;
  &:hover{border-color:#2563eb;}
`;
const GiftThumb = styled.img`width:28px;height:28px;border-radius:6px;object-fit:cover;flex-shrink:0;`;
const GiftThumbPlaceholder = styled.div`
  width:28px;height:28px;border-radius:6px;
  background:linear-gradient(135deg,#bfdbfe,#93c5fd);
  display:flex;align-items:center;justify-content:center;font-size:14px;flex-shrink:0;
`;
const GiftDropdown = styled.div`
  position:absolute;top:calc(100% + 4px);left:0;right:0;
  background:white;border:1.5px solid #e2e8f0;border-radius:12px;
  z-index:100;overflow:hidden;box-shadow:0 8px 24px rgba(0,0,0,.12);
  max-height:280px;overflow-y:auto;
`;
const GiftOption = styled.button<{$active:boolean}>`
  display:flex;align-items:center;gap:10px;padding:8px 12px;width:100%;
  background:${p=>p.$active?'#eff6ff':'transparent'};
  border:none;color:#1e293b;font-size:13px;cursor:pointer;text-align:left;
  transition:background .1s;
  &:hover{background:#f0f9ff;}
`;
const GiftOptionImg = styled.img`width:36px;height:36px;border-radius:8px;object-fit:cover;flex-shrink:0;`;
const GiftOptionPlaceholder = styled.div`
  width:36px;height:36px;border-radius:8px;
  background:linear-gradient(135deg,#bfdbfe,#93c5fd);
  display:flex;align-items:center;justify-content:center;font-size:18px;flex-shrink:0;
`;
const NoneOption = styled.button`
  display:flex;align-items:center;gap:10px;padding:8px 12px;width:100%;
  background:transparent;border:none;color:#64748b;font-size:13px;cursor:pointer;text-align:left;
  &:hover{background:#f8fafc;}
  border-bottom:1px solid #f1f5f9;
`;
const SaveBtn = styled.button`
  width:100%;padding:14px;
  background:#2563eb;
  border:none;border-radius:12px;color:white;font-weight:800;font-size:16px;
  cursor:pointer;transition:all .2s;
  &:hover{background:#1d4ed8;box-shadow:0 4px 14px rgba(37,99,235,.35);transform:translateY(-1px);}
  &:disabled{opacity:.5;cursor:default;transform:none;box-shadow:none;}
`;
const InfoBox = styled.div`
  background:#eff6ff;border:1px solid #bfdbfe;
  border-radius:10px;padding:10px 14px;font-size:13px;color:#1d4ed8;margin-bottom:20px;
`;
const PrevGiftBadge = styled.div`
  display:flex;align-items:center;gap:5px;
  font-size:11px;color:#64748b;margin-top:4px;
  padding:3px 7px;background:#f1f5f9;border-radius:8px;
  border:1px solid #e2e8f0;max-width:220px;
`;

// ── Types ─────────────────────────────────────────────────────────────────────
interface Gift { id:number; name:string; quantity:number; image?:string|null; note?:string|null; }
interface StudentRow { studentId:number; name:string; isPresent:boolean; giftId:number|null; }
interface PrevGift { studentId:number; giftId:number; giftName:string; giftedAt:string; }

function imgUrl(img?:string|null){ if(!img) return null; if(img.startsWith('http')||img.startsWith('data:')) return img; return `${getApiUrl()}${img}`; }

// ── Sub-component: GiftPicker dropdown ────────────────────────────────────────
const GiftPicker: React.FC<{gifts:Gift[];value:number|null;onChange:(id:number|null)=>void}> = ({gifts,value,onChange}) => {
  const [open,setOpen] = useState(false);
  const chosen = gifts.find(g=>g.id===value)||null;
  return (
    <GiftPickerWrap>
      <GiftPickerBtn $hasGift={!!chosen} onClick={()=>setOpen(o=>!o)}>
        {chosen ? (<>
          {imgUrl(chosen.image)
            ? <GiftThumb src={imgUrl(chosen.image)!} alt={chosen.name}/>
            : <GiftThumbPlaceholder>🎁</GiftThumbPlaceholder>
          }
          <span style={{flex:1,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{chosen.name}</span>
          <span style={{color:'#34d399',fontSize:11,flexShrink:0}}>còn {chosen.quantity}</span>
        </>) : (
          <><GiftThumbPlaceholder>🎁</GiftThumbPlaceholder><span style={{color:'#64748b'}}>Chọn quà tặng...</span></>
        )}
        <span style={{marginLeft:'auto',color:'#64748b',fontSize:12}}>{open?'▲':'▼'}</span>
      </GiftPickerBtn>
      {open && (
        <GiftDropdown>
          <NoneOption onClick={()=>{onChange(null);setOpen(false);}}>
            <GiftThumbPlaceholder style={{background:'rgba(255,255,255,.05)'}}>🚫</GiftThumbPlaceholder>
            Không tặng quà
          </NoneOption>
          {gifts.map(g=>(
            <GiftOption key={g.id} $active={value===g.id} onClick={()=>{onChange(g.id);setOpen(false);}}>
              {imgUrl(g.image)
                ? <GiftOptionImg src={imgUrl(g.image)!} alt={g.name}/>
                : <GiftOptionPlaceholder>🎁</GiftOptionPlaceholder>
              }
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontWeight:600,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{g.name}</div>
                <div style={{fontSize:11,color:g.quantity===0?'#ef4444':'#34d399'}}>
                  {g.quantity===0?'⚠️ Hết kho':`📦 Còn ${g.quantity}`}
                </div>
              </div>
            </GiftOption>
          ))}
        </GiftDropdown>
      )}
    </GiftPickerWrap>
  );
};

// ── Main component ────────────────────────────────────────────────────────────
interface Props { gifts: Gift[]; onGiftsUpdated: () => void; }

const GiftToClass: React.FC<Props> = ({ gifts, onGiftsUpdated }) => {
  const dispatch = useAppDispatch();
  const classes = useAppSelector((s:any)=>s.classes.classesList||[]);
  const allStudents = useAppSelector((s:any)=>s.students.studentsList||[]);

  const [classId, setClassId] = useState('');
  const [rows, setRows] = useState<StudentRow[]>([]);
  const [prevGifts, setPrevGifts] = useState<PrevGift[]>([]); // quà đã tặng trước đó
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [hasAttendance, setHasAttendance] = useState<boolean|null>(null);

  useEffect(()=>{ dispatch(classActions.getAll()); },[dispatch]);

  // Khi chọn lớp → fetch điểm danh hôm nay + quà đã tặng trước đó
  useEffect(()=>{
    if(!classId) { setRows([]); setPrevGifts([]); setHasAttendance(null); return; }
    const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD
    setLoading(true); setHasAttendance(null);
    fetchWithAuth(`${getApiUrl()}/api/attendance_session/${classId}/${today}`,{headers:HEADERS})
      .then(r=>r.json())
      .then(async data=>{
        let rowsData: StudentRow[];
        if(data?.studentRecords){
          setHasAttendance(true);
          const presentIds: number[] = data.studentRecords
            .filter((r:any)=>r.isPresent)
            .map((r:any)=>r.studentId);
          const allIds: number[] = data.studentRecords.map((r:any)=>r.studentId);
          const presentSet = new Set(presentIds);
          rowsData = allIds.map((sid:number)=>{
            const stu = allStudents.find((s:any)=>s.id===sid);
            return { studentId:sid, name:stu?.name||`Học sinh #${sid}`, isPresent:presentSet.has(sid), giftId:null };
          }).sort((a,b)=>(b.isPresent?1:0)-(a.isPresent?1:0));
        } else {
          setHasAttendance(false);
          const classStudents = allStudents.filter((s:any)=>String(s.classId)===classId&&!s.archivedAt);
          rowsData = classStudents.map((s:any)=>({studentId:s.id,name:s.name,isPresent:true,giftId:null}));
        }
        setRows(rowsData);
        // Fetch quà đã tặng trước đó cho từng học sinh
        const sids = rowsData.map(r=>r.studentId);
        if(sids.length>0){
          try{
            const txs = await giftService.getTransactionsByStudents(sids);
            setPrevGifts(txs.map((tx:any)=>({ studentId:tx.studentId, giftId:tx.giftId, giftName:tx.giftName, giftedAt:tx.giftedAt })));
          }catch{ setPrevGifts([]); }
        }
      })
      .catch(()=>{
        setHasAttendance(false);
        const classStudents = allStudents.filter((s:any)=>String(s.classId)===classId&&!s.archivedAt);
        setRows(classStudents.map((s:any)=>({studentId:s.id,name:s.name,isPresent:true,giftId:null})));
        setPrevGifts([]);
      })
      .finally(()=>setLoading(false));
  },[classId, allStudents]);

  const setGiftForStudent = (studentId:number, giftId:number|null) => {
    setRows(prev=>prev.map(r=>r.studentId===studentId?{...r,giftId}:r));
  };

  const setGiftForAll = (giftId:number|null) => {
    setRows(prev=>prev.map(r=>r.isPresent?{...r,giftId}:r));
  };

  // Tính summary: SL mỗi loại quà sẽ dùng
  const giftSummary = rows
    .filter(r=>r.isPresent&&r.giftId!==null)
    .reduce<Record<number,number>>((acc,r)=>{acc[r.giftId!]=(acc[r.giftId!]||0)+1;return acc;},{});

  const handleSave = async () => {
    const toGive = rows.filter(r=>r.isPresent&&r.giftId!==null);
    if(toGive.length===0){ dispatch(notificationActions.addNotification('Chưa chọn quà cho học sinh nào!','DANGER')); return; }
    // Kiểm tra kho đủ không
    for(const [gid,qty] of Object.entries(giftSummary)){
      const g = gifts.find(x=>x.id===Number(gid));
      if(g && g.quantity<qty){
        dispatch(notificationActions.addNotification(`Kho "${g.name}" chỉ còn ${g.quantity}, không đủ tặng ${qty}!`,'DANGER'));
        return;
      }
    }
    setSaving(true);
    try{
      await Promise.all(toGive.map(r=>{
        const stu = allStudents.find((s:any)=>s.id===r.studentId);
        return giftService.giveGift(r.giftId!,{studentId:r.studentId,studentName:stu?.name||String(r.studentId),quantity:1});
      }));
      dispatch(notificationActions.addNotification(`✅ Đã tặng quà cho ${toGive.length} học sinh thành công!`,'SUCCESS'));
      // Reset selections
      setRows(prev=>prev.map(r=>({...r,giftId:null})));
      onGiftsUpdated(); // notify parent refresh gifts
    }catch(e:any){
      dispatch(notificationActions.addNotification(String(e)||'Lỗi khi tặng quà','DANGER'));
    }
    setSaving(false);
  };

  const toGiveCount = rows.filter(r=>r.isPresent&&r.giftId!==null).length;
  const availableGifts = gifts.filter(g=>g.quantity>0);

  return (
    <Wrap>
      <Card>
        <div style={{marginBottom:20}}>
          <Label>Chọn lớp học</Label>
          <Select value={classId} onChange={e=>setClassId(e.target.value)}>
            <option value=''>-- Chọn lớp --</option>
            {[...classes].sort((a:any,b:any)=>a.name.localeCompare(b.name)).map((c:any)=>(
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </Select>
        </div>

        {classId && hasAttendance===true && (
          <InfoBox>📋 Hôm nay có điểm danh — hiển thị học sinh có mặt trước, học sinh vắng mờ.</InfoBox>
        )}
        {classId && hasAttendance===false && (
          <InfoBox style={{background:'rgba(251,191,36,.08)',borderColor:'rgba(251,191,36,.2)',color:'#fbbf24'}}>
            ℹ️ Chưa có điểm danh hôm nay — hiển thị tất cả học sinh trong lớp.
          </InfoBox>
        )}

        {/* Nút tặng đồng loạt */}
        {rows.length > 0 && (
          <div style={{display:'flex',alignItems:'center',gap:12,marginBottom:16,flexWrap:'wrap'}}>
            <span style={{fontSize:13,color:'#94a3b8',fontWeight:700}}>Chọn quà cho tất cả:</span>
            <div style={{flex:1,minWidth:200}}>
              <GiftPicker gifts={availableGifts} value={null} onChange={setGiftForAll} />
            </div>
          </div>
        )}
      </Card>

      {loading && <Empty>⏳ Đang tải điểm danh...</Empty>}

      {!loading && rows.length > 0 && (
        <Card>
          <div style={{fontSize:14,fontWeight:700,color:'#94a3b8',marginBottom:16}}>
            👨‍🎓 {rows.filter(r=>r.isPresent).length} học sinh có mặt
            {rows.filter(r=>!r.isPresent).length>0 && ` / ${rows.filter(r=>!r.isPresent).length} vắng`}
          </div>

          {rows.map(row=>{
            const prev = prevGifts.find(p=>p.studentId===row.studentId);
            const prevGiftObj = prev ? gifts.find(g=>g.id===prev.giftId) : null;
            const prevImgUrl = prevGiftObj ? imgUrl(prevGiftObj.image) : null;
            return (
            <StudentRow key={row.studentId} style={{opacity:row.isPresent?1:0.4,alignItems:'flex-start'}}>
              <Avatar style={{marginTop:2}}>{row.name.charAt(0).toUpperCase()}</Avatar>
              <div style={{flex:1,minWidth:0}}>
                <StudentName>
                  {row.name}
                  {!row.isPresent && <AbsentBadge style={{marginLeft:8}}>Vắng</AbsentBadge>}
                </StudentName>
                {prev && (
                  <PrevGiftBadge title={`Tặng lúc: ${new Date(prev.giftedAt).toLocaleDateString('vi-VN')}`}>
                    {prevImgUrl
                      ? <img src={prevImgUrl} alt={prev.giftName} style={{width:14,height:14,borderRadius:3,objectFit:'cover'}}/>
                      : <span style={{fontSize:12}}>🎁</span>}
                    <span>Đã tặng: <b>{prev.giftName}</b></span>
                  </PrevGiftBadge>
                )}
              </div>
              {row.isPresent ? (
                <GiftPicker
                  gifts={availableGifts}
                  value={row.giftId}
                  onChange={gid=>setGiftForStudent(row.studentId,gid)}
                />
              ) : (
                <span style={{fontSize:12,color:'#334155'}}>Không tặng</span>
              )}
            </StudentRow>
          );})}  

          {/* Summary */}
          {toGiveCount > 0 && (
            <div style={{marginTop:16,background:'rgba(52,211,153,.08)',border:'1px solid rgba(52,211,153,.2)',borderRadius:10,padding:'10px 14px'}}>
              <div style={{fontSize:13,color:'#6ee7b7',fontWeight:700,marginBottom:6}}>📦 Tổng quà sẽ dùng:</div>
              {Object.entries(giftSummary).map(([gid,qty])=>{
                const g=gifts.find(x=>x.id===Number(gid));
                if(!g) return null;
                const isEnough=g.quantity>=qty;
                return (
                  <div key={gid} style={{fontSize:12,color:isEnough?'#94a3b8':'#fca5a5',marginBottom:2}}>
                    {isEnough?'✅':'⚠️'} {g.name}: {qty} cái (kho còn {g.quantity})
                  </div>
                );
              })}
              <div style={{fontSize:12,color:'#6ee7b7',marginTop:6}}>→ Tổng: {toGiveCount} học sinh</div>
            </div>
          )}

          <SaveBtn
            disabled={toGiveCount===0||saving}
            style={{marginTop:20}}
            onClick={handleSave}
          >
            {saving?'⏳ Đang lưu...':`🎀 Tặng quà cho ${toGiveCount} học sinh`}
          </SaveBtn>
        </Card>
      )}

      {!loading && classId && rows.length===0 && (
        <Empty>📭 Không có học sinh nào trong lớp này</Empty>
      )}
    </Wrap>
  );
};

export default GiftToClass;
